import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  IconButton,
  Grid,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../context/AuthContext';

const RegisterModal = () => {
  const { showRegisterModal, closeModals, openLoginModal, register, requestOTP } = useAuth();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    FirstName: '',
    LastName: '',
    Email: '',
    Mobile: '',
    Gender: '',
    Password: '',
    Address: '',
    City: '',
    State: '',
    PinCode: '',
    ReferenceBy: '',
  });
  const [otp, setOtp] = useState('');
  const [randomOTP, setRandomOTP] = useState(0);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [statesData, setStatesData] = useState([]);
  const [errors, setErrors] = useState({});
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    fetch('/states.json')
      .then((response) => response.json())
      .then((data) => {
        setStatesData(data.states);
        setStates(data.states.map((state) => state.state));
      })
      .catch((error) => console.error('Error fetching states:', error));
  }, []);

  // Timer effect for OTP
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Validation
    if (name === 'Email') {
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
        setErrors({ ...errors, Email: 'Invalid email address' });
      } else {
        setErrors({ ...errors, Email: '' });
      }
    }
    if (name === 'Mobile') {
      if (!/^\d{10}$/.test(value)) {
        setErrors({ ...errors, Mobile: 'Invalid mobile number (10 digits required)' });
      } else {
        setErrors({ ...errors, Mobile: '' });
      }
    }
  };

  const handleStateChange = (e) => {
    const state = e.target.value;
    setFormData({ ...formData, State: state, City: '' });
    const selectedStateData = statesData.find((item) => item.state === state);
    setCities(selectedStateData ? selectedStateData.districts : []);
  };

  const handleSendOTP = async () => {
    if (!formData.Email) {
      setErrors({ ...errors, Email: 'Valid email required' });
      return;
    }
    setLoading(true);
    const result = await requestOTP(formData.Email);
    if (result.success) {
      setRandomOTP(result.otp);
      setStep(2);
      setTimer(120); // 2 minutes timer
      setCanResend(false);
    }
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    await register(formData, otp, randomOTP);
    setLoading(false);
    resetForm();
  };

  const resetForm = () => {
    setStep(1);
    setOtp('');
    setRandomOTP(0);
    setTimer(0);
    setCanResend(false);
    setFormData({
      FirstName: '',
      LastName: '',
      Email: '',
      Mobile: '',
      Gender: '',
      Password: '',
      Address: '',
      City: '',
      State: '',
      PinCode: '',
      ReferenceBy: '',
    });
  };

  const handleClose = () => {
    resetForm();
    closeModals();
  };

  const handleSwitchToLogin = () => {
    resetForm();
    openLoginModal();
  };

  return (
    <Dialog
      open={showRegisterModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {step === 2 && (
            <IconButton onClick={() => setStep(1)} size="small">
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h5" fontWeight="bold" color="#5D4037">
            {step === 1 ? 'Create Account' : 'Verify OTP'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {step === 1 ? (
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="FirstName"
                  value={formData.FirstName}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="LastName"
                  value={formData.LastName}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="Email"
                  type="email"
                  value={formData.Email}
                  onChange={handleChange}
                  required
                  size="small"
                  error={!!errors.Email}
                  helperText={errors.Email}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="Mobile"
                  value={formData.Mobile}
                  onChange={handleChange}
                  required
                  size="small"
                  error={!!errors.Mobile}
                  helperText={errors.Mobile}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  name="Password"
                  type="password"
                  value={formData.Password}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl size="small">
                  <FormLabel>Gender</FormLabel>
                  <RadioGroup row name="Gender" value={formData.Gender} onChange={handleChange}>
                    <FormControlLabel value="male" control={<Radio size="small" />} label="Male" />
                    <FormControlLabel value="female" control={<Radio size="small" />} label="Female" />
                    <FormControlLabel value="other" control={<Radio size="small" />} label="Other" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="Address"
                  value={formData.Address}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="State"
                  name="State"
                  value={formData.State}
                  onChange={handleStateChange}
                  required
                  size="small"
                >
                  {states.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="City"
                  name="City"
                  value={formData.City}
                  onChange={handleChange}
                  required
                  size="small"
                  disabled={!formData.State}
                >
                  {cities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Pin Code"
                  name="PinCode"
                  value={formData.PinCode}
                  onChange={handleChange}
                  required
                  size="small"
                  inputProps={{ maxLength: 6 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Referral Code (Optional)"
                  name="ReferenceBy"
                  value={formData.ReferenceBy}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
            </Grid>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSendOTP}
              disabled={loading || !formData.FirstName || !formData.Email || !formData.Mobile || !formData.Password}
              sx={{
                mt: 3,
                py: 1.5,
                bgcolor: '#8B4513',
                '&:hover': { bgcolor: '#6d3610' },
                fontWeight: 'bold',
                borderRadius: 2,
              }}
            >
              {loading ? 'Sending OTP...' : 'Continue'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Button
                  onClick={handleSwitchToLogin}
                  sx={{ textTransform: 'none', color: '#8B4513', fontWeight: 'bold' }}
                >
                  Sign In
                </Button>
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              We've sent a 6-digit OTP to <strong>{formData.Email}</strong>
            </Typography>

            {/* Timer Display */}
            {timer > 0 && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  OTP expires in:
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="error">
                  {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}
                </Typography>
              </Box>
            )}

            {timer === 0 && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                OTP has expired. Please request a new one.
              </Typography>
            )}

            <TextField
              fullWidth
              label="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 6 }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyOTP}
              disabled={loading || otp.length !== 6 || timer === 0}
              sx={{
                py: 1.5,
                bgcolor: '#8B4513',
                '&:hover': { bgcolor: '#6d3610' },
                fontWeight: 'bold',
                borderRadius: 2,
              }}
            >
              {loading ? 'Verifying...' : 'Verify & Create Account'}
            </Button>
            <Button
              onClick={handleSendOTP}
              disabled={!canResend || loading}
              sx={{
                mt: 2,
                textTransform: 'none',
                color: canResend ? '#8B4513' : 'text.disabled',
              }}
            >
              {canResend ? 'Resend OTP' : `Resend OTP in ${timer}s`}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RegisterModal;
